/** Owner-only Day-1 target and analysis-run control surface. */

import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import {
  ApiFailure,
  createAnalysisBusiness,
  createAnalysisRun,
  fetchAnalysisBusinesses,
  fetchAnalysisRuns,
  newIdempotencyKey,
  type AnalysisBusiness,
} from '../api.js';
import { Button, EmptyState, InlineAlert, TextAreaField, TextField } from '../ui.js';
import {
  FailurePanel,
  LoadingPanel,
  PageTitle,
  SectionHeading,
  useAsync,
} from './common.js';

const card: CSSProperties = {
  border: '1px solid var(--border-hairline)',
  borderRadius: 'var(--radius-card)',
  padding: 'var(--space-md)',
  background: 'var(--surface-card)',
};

const selectStyle: CSSProperties = {
  width: '100%',
  minHeight: 'var(--field-height)',
  padding: '10px 14px',
  borderRadius: 'var(--radius-input)',
  border: '1px solid var(--border-input)',
  background: 'var(--surface-card)',
  color: 'var(--text-primary)',
  font: 'var(--type-body-md)',
};

interface OfferDraft {
  readonly key: string;
  readonly title: string;
  readonly offerType: string;
  readonly description: string;
  readonly deliveryMode: string;
}

interface SourceDraft {
  readonly key: string;
  readonly platform: string;
  readonly canonicalUrl: string;
  readonly externalIdentity: string;
}

function freshOffer(): OfferDraft {
  return {
    key: crypto.randomUUID(),
    title: '',
    offerType: 'SERVICE',
    description: '',
    deliveryMode: '',
  };
}

function freshSource(): SourceDraft {
  return {
    key: crypto.randomUUID(),
    platform: 'WEBSITE',
    canonicalUrl: '',
    externalIdentity: '',
  };
}

function fieldLabel(label: string, children: ReactNode): ReactNode {
  return (
    <label
      style={{
        display: 'block',
        font: 'var(--type-caption)',
        color: 'var(--text-secondary)',
      }}
    >
      <span style={{ display: 'block', marginBottom: 6 }}>{label}</span>
      {children}
    </label>
  );
}

function selectedBusiness(
  businesses: readonly AnalysisBusiness[],
  id: string,
): AnalysisBusiness | null {
  return businesses.find((business) => business.id === id) ?? null;
}

export function AnalysisPage(): ReactNode {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const copy =
    language === 'ar'
      ? {
          title: 'تشغيل التحليل',
          subtitle: 'أنشئ هدفاً واضحاً ثم افتح تشغيل تحليل قابل للاستئناف.',
          target: 'الأنشطة المستهدفة',
          newTarget: 'إضافة نشاط',
          businessName: 'اسم النشاط',
          category: 'الفئة',
          geography: 'النطاق الجغرافي',
          offers: 'العروض',
          offerTitle: 'اسم العرض',
          offerType: 'نوع العرض',
          description: 'الوصف',
          delivery: 'طريقة التقديم',
          sources: 'المصادر',
          platform: 'المنصة',
          url: 'رابط المصدر',
          identity: 'المعرّف الخارجي (اختياري)',
          addOffer: 'إضافة عرض',
          addSource: 'إضافة مصدر',
          remove: 'إزالة',
          saveTarget: 'حفظ النشاط',
          start: 'بدء تشغيل جديد',
          chooseTarget: 'اختر النشاط',
          chooseOffer: 'كل العروض / لا يوجد عرض أساسي',
          objective: 'هدف التحليل',
          begin: 'إنشاء التشغيل',
          runs: 'التشغيلات',
          noRuns: 'لا توجد تشغيلات تحليل بعد.',
          open: 'فتح',
          emptyTargets: 'أضف أول نشاط مستهدف للبدء.',
        }
      : {
          title: 'Analysis runs',
          subtitle: 'Define a clear target, then open a durable run you can resume.',
          target: 'Target businesses',
          newTarget: 'Add business',
          businessName: 'Business name',
          category: 'Category',
          geography: 'Geography',
          offers: 'Offers',
          offerTitle: 'Offer title',
          offerType: 'Offer type',
          description: 'Description',
          delivery: 'Delivery mode',
          sources: 'Sources',
          platform: 'Platform',
          url: 'Source URL',
          identity: 'External identity (optional)',
          addOffer: 'Add offer',
          addSource: 'Add source',
          remove: 'Remove',
          saveTarget: 'Save business',
          start: 'Start a new run',
          chooseTarget: 'Choose business',
          chooseOffer: 'All offers / no primary offer',
          objective: 'Analysis objective',
          begin: 'Create run',
          runs: 'Runs',
          noRuns: 'No analysis runs yet.',
          open: 'Open',
          emptyTargets: 'Add the first target business to begin.',
        };

  const load = useCallback(async () => {
    const [businesses, runs] = await Promise.all([
      fetchAnalysisBusinesses(),
      fetchAnalysisRuns(),
    ]);
    return { businesses: businesses.businesses, runs: runs.runs };
  }, []);
  const { state, reload } = useAsync(load);
  const [showBusinessForm, setShowBusinessForm] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [geography, setGeography] = useState('Saudi Arabia');
  const [offers, setOffers] = useState<readonly OfferDraft[]>([]);
  const [sources, setSources] = useState<readonly SourceDraft[]>([freshSource()]);
  const [targetId, setTargetId] = useState('');
  const [offerId, setOfferId] = useState('');
  const [objective, setObjective] = useState('');
  const [busy, setBusy] = useState<'business' | 'run' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const businesses = state.status === 'ready' ? state.value.businesses : [];
  const target = useMemo(
    () => selectedBusiness(businesses, targetId),
    [businesses, targetId],
  );

  const patchOffer = useCallback((key: string, patch: Partial<OfferDraft>) => {
    setOffers((current) =>
      current.map((offer) => (offer.key === key ? { ...offer, ...patch } : offer)),
    );
  }, []);
  const patchSource = useCallback((key: string, patch: Partial<SourceDraft>) => {
    setSources((current) =>
      current.map((source) => (source.key === key ? { ...source, ...patch } : source)),
    );
  }, []);

  const submitBusiness = useCallback(async () => {
    setError(null);
    setBusy('business');
    try {
      await createAnalysisBusiness(newIdempotencyKey(), {
        displayName: name,
        ...(category.trim() === '' ? {} : { category }),
        ...(geography.trim() === '' ? {} : { geography }),
        offers: offers.map((offer) => ({
          title: offer.title,
          offerType: offer.offerType,
          ...(offer.description.trim() === ''
            ? {}
            : { description: offer.description }),
          ...(offer.deliveryMode.trim() === ''
            ? {}
            : { deliveryMode: offer.deliveryMode }),
        })),
        sources: sources.map((source) => ({
          platform: source.platform,
          relationship: 'OWNED',
          canonicalUrl: source.canonicalUrl,
          ...(source.externalIdentity.trim() === ''
            ? {}
            : { externalIdentity: source.externalIdentity }),
        })),
      });
      setName('');
      setCategory('');
      setOffers([]);
      setSources([freshSource()]);
      setShowBusinessForm(false);
      reload();
    } catch (failure) {
      setError(
        failure instanceof ApiFailure
          ? (failure.detail ?? failure.message)
          : 'The business could not be saved.',
      );
    } finally {
      setBusy(null);
    }
  }, [name, category, geography, offers, sources, reload]);

  const submitRun = useCallback(async () => {
    setError(null);
    setBusy('run');
    try {
      const result = await createAnalysisRun(newIdempotencyKey(), {
        targetBusinessId: targetId,
        ...(offerId === '' ? {} : { primaryOfferId: offerId }),
        objective,
      });
      void navigate(`/analysis/${result.run.id}`);
    } catch (failure) {
      setError(
        failure instanceof ApiFailure
          ? (failure.detail ?? failure.message)
          : 'The run could not be created.',
      );
    } finally {
      setBusy(null);
    }
  }, [targetId, offerId, objective, navigate]);

  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed')
    return <FailurePanel failure={state.failure} onRetry={reload} />;

  return (
    <>
      <PageTitle>{copy.title}</PageTitle>
      <p
        style={{
          color: 'var(--text-secondary)',
          margin: '-12px 0 var(--space-lg)',
          font: 'var(--type-body-lg)',
        }}
      >
        {copy.subtitle}
      </p>
      {error ? <InlineAlert tone="error" title={error} role="alert" /> : null}

      <section style={{ ...card, marginBottom: 'var(--space-lg)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <SectionHeading>{copy.target}</SectionHeading>
          <Button
            variant="outline"
            onClick={() => {
              setShowBusinessForm((open) => !open);
            }}
          >
            {copy.newTarget}
          </Button>
        </div>
        {businesses.length === 0 && !showBusinessForm ? (
          <EmptyState title={copy.emptyTargets} compact />
        ) : null}
        {showBusinessForm ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submitBusiness();
            }}
          >
            <div className="mj-form-two-column">
              <TextField
                label={copy.businessName}
                value={name}
                onChange={setName}
                required
                maxLength={200}
              />
              <TextField
                label={copy.category}
                value={category}
                onChange={setCategory}
                maxLength={160}
              />
              <TextField
                label={copy.geography}
                value={geography}
                onChange={setGeography}
                maxLength={160}
              />
            </div>
            <SectionHeading>{copy.offers}</SectionHeading>
            {offers.map((offer, index) => (
              <div
                key={offer.key}
                style={{
                  ...card,
                  background: 'var(--surface-subtle)',
                  marginBottom: 12,
                }}
              >
                <div className="mj-form-two-column">
                  <TextField
                    label={`${copy.offerTitle} ${index + 1}`}
                    value={offer.title}
                    onChange={(value) => {
                      patchOffer(offer.key, { title: value });
                    }}
                    required
                    maxLength={200}
                  />
                  {fieldLabel(
                    copy.offerType,
                    <select
                      value={offer.offerType}
                      onChange={(event) => {
                        patchOffer(offer.key, { offerType: event.target.value });
                      }}
                      style={selectStyle}
                    >
                      <option value="SERVICE">Service</option>
                      <option value="PROGRAM">Program</option>
                      <option value="PHYSICAL_PRODUCT">Physical product</option>
                      <option value="DIGITAL_PRODUCT">Digital product</option>
                    </select>,
                  )}
                  <TextField
                    label={copy.delivery}
                    value={offer.deliveryMode}
                    onChange={(value) => {
                      patchOffer(offer.key, { deliveryMode: value });
                    }}
                    maxLength={160}
                  />
                  <TextField
                    label={copy.description}
                    value={offer.description}
                    onChange={(value) => {
                      patchOffer(offer.key, { description: value });
                    }}
                    maxLength={4000}
                  />
                </div>
                {offers.length > 0 ? (
                  <Button
                    size="sm"
                    variant="quiet"
                    onClick={() => {
                      setOffers((current) =>
                        current.filter((item) => item.key !== offer.key),
                      );
                    }}
                  >
                    {copy.remove}
                  </Button>
                ) : null}
              </div>
            ))}
            <Button
              size="sm"
              variant="quiet"
              onClick={() => {
                setOffers((current) => [...current, freshOffer()]);
              }}
            >
              {copy.addOffer}
            </Button>

            <SectionHeading>{copy.sources}</SectionHeading>
            {sources.map((source, index) => (
              <div
                key={source.key}
                style={{
                  ...card,
                  background: 'var(--surface-subtle)',
                  marginBottom: 12,
                }}
              >
                <div className="mj-form-two-column">
                  {fieldLabel(
                    `${copy.platform} ${index + 1}`,
                    <select
                      value={source.platform}
                      onChange={(event) => {
                        patchSource(source.key, { platform: event.target.value });
                      }}
                      style={selectStyle}
                    >
                      <option value="WEBSITE">Website</option>
                      <option value="INSTAGRAM">Instagram</option>
                      <option value="TIKTOK">TikTok</option>
                    </select>,
                  )}
                  <TextField
                    label={copy.url}
                    value={source.canonicalUrl}
                    onChange={(value) => {
                      patchSource(source.key, { canonicalUrl: value });
                    }}
                    type="url"
                    dir="ltr"
                    required
                    maxLength={2048}
                  />
                  <TextField
                    label={copy.identity}
                    value={source.externalIdentity}
                    onChange={(value) => {
                      patchSource(source.key, { externalIdentity: value });
                    }}
                    dir="ltr"
                    maxLength={500}
                  />
                </div>
                {sources.length > 1 ? (
                  <Button
                    size="sm"
                    variant="quiet"
                    onClick={() => {
                      setSources((current) =>
                        current.filter((item) => item.key !== source.key),
                      );
                    }}
                  >
                    {copy.remove}
                  </Button>
                ) : null}
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <Button
                size="sm"
                variant="quiet"
                onClick={() => {
                  setSources((current) => [...current, freshSource()]);
                }}
              >
                {copy.addSource}
              </Button>
              <Button type="submit" variant="primary" busy={busy === 'business'}>
                {copy.saveTarget}
              </Button>
            </div>
          </form>
        ) : null}
      </section>

      {businesses.length > 0 ? (
        <section style={{ ...card, marginBottom: 'var(--space-lg)' }}>
          <SectionHeading>{copy.start}</SectionHeading>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submitRun();
            }}
          >
            <div className="mj-form-two-column">
              {fieldLabel(
                copy.chooseTarget,
                <select
                  required
                  value={targetId}
                  onChange={(event) => {
                    setTargetId(event.target.value);
                    setOfferId('');
                  }}
                  style={selectStyle}
                >
                  <option value="">—</option>
                  {businesses.map((business) => (
                    <option key={business.id} value={business.id}>
                      {business.displayName}
                    </option>
                  ))}
                </select>,
              )}
              {fieldLabel(
                copy.chooseOffer,
                <select
                  value={offerId}
                  onChange={(event) => {
                    setOfferId(event.target.value);
                  }}
                  disabled={target === null}
                  style={selectStyle}
                >
                  <option value="">{copy.chooseOffer}</option>
                  {target?.offers.map((offer) => (
                    <option key={offer.id} value={offer.id}>
                      {offer.title}
                    </option>
                  ))}
                </select>,
              )}
            </div>
            <TextAreaField
              label={copy.objective}
              value={objective}
              onChange={setObjective}
              rows={3}
              maxLength={2000}
            />
            <Button
              type="submit"
              variant="primary"
              busy={busy === 'run'}
              disabled={targetId === '' || objective.trim() === ''}
            >
              {copy.begin}
            </Button>
          </form>
        </section>
      ) : null}

      <section>
        <SectionHeading>{copy.runs}</SectionHeading>
        {state.value.runs.length === 0 ? (
          <EmptyState title={copy.noRuns} />
        ) : (
          <div className="mj-dashboard-grid">
            {state.value.runs.map((run) => (
              <article key={run.id} style={card}>
                <h3
                  style={{
                    font: 'var(--type-heading-sm)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {run.targetBusinessName}
                </h3>
                <p style={{ color: 'var(--text-secondary)', margin: '8px 0' }}>
                  {run.objective}
                </p>
                <p
                  style={{
                    font: 'var(--type-caption)',
                    color: 'var(--text-muted)',
                    marginBottom: 12,
                  }}
                >
                  {run.currentStage} · {run.executionState}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void navigate(`/analysis/${run.id}`)}
                >
                  {copy.open}
                </Button>
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
